import logger from "./logger";
import { getCrashContext } from "./crashContext";

export type CrashType = "render" | "uncaught" | "unhandledrejection";

const MAX_STACK_LENGTH = 4000;

const MESSAGE_BY_TYPE: Record<CrashType, string> = {
  render: "React render crash",
  uncaught: "Uncaught error",
  unhandledrejection: "Unhandled promise rejection",
};

interface NormalizedError {
  errorName: string;
  errorMessage: string;
  stack: string;
}

// Stringify a non-Error throw/rejection. Plain objects (e.g. `reject({ code: 500 })`)
// would become a useless "[object Object]" via String(), so JSON-encode objects —
// guarding against circular refs — while leaving primitives as readable strings.
const stringifyNonError = (input: unknown): string => {
  if (typeof input === "object" && input !== null) {
    try {
      return JSON.stringify(input);
    } catch {
      return String(input);
    }
  }
  return String(input);
};

// Coerce anything that was thrown/rejected into a stable shape. JS lets you
// `throw "string"` or reject with a non-Error, so we can't assume `.message`.
export const normalizeError = (input: unknown): NormalizedError => {
  if (input instanceof Error) {
    return {
      errorName: input.name,
      errorMessage: input.message,
      stack: (input.stack ?? "").slice(0, MAX_STACK_LENGTH),
    };
  }
  return { errorName: "NonError", errorMessage: stringifyNonError(input), stack: "" };
};

export const reportCrash = async (
  type: CrashType,
  error: unknown,
  extra: Record<string, unknown> = {},
): Promise<void> => {
  try {
    logger.error(MESSAGE_BY_TYPE[type], {
      type,
      ...normalizeError(error),
      ...extra,
      context: getCrashContext(),
    });
    await logger.flush();
  } catch {
    // A crash reporter must never crash the app further.
  }
};

let handlersInstalled = false;

export const installGlobalCrashHandlers = (): void => {
  if (handlersInstalled) return;
  handlersInstalled = true;

  // Known limitation: reportCrash's logger.flush() is fire-and-forget here (void).
  // If a fatal error coincides with an immediate page unload/navigation, the
  // Logtail send may not complete and that crash log can be lost. Acceptable for
  // v1 (most uncaught errors don't unload the page); harden with sendBeacon-style
  // delivery if we observe dropped reports.

  window.addEventListener("error", (event: ErrorEvent) => {
    const source = `${event.filename ?? ""}:${event.lineno ?? 0}:${event.colno ?? 0}`;
    void reportCrash("uncaught", event.error ?? event.message, { source });
  });

  window.addEventListener("unhandledrejection", (event: PromiseRejectionEvent) => {
    void reportCrash("unhandledrejection", event.reason);
  });
};
